import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageDropzone } from "@/components/ui/image-dropzone";
import {
  Apple, Beef, Cake, Coffee, Cookie, CupSoda, Egg, Fish, Flame,
  Grape, IceCream, Leaf, Milk, Pizza, Salad, Sandwich, Soup, UtensilsCrossed,
  Wheat, Cherry, ImagePlus, X
} from "lucide-react";

const AVAILABLE_ICONS = [
  { name: "apple", icon: Apple, label: "Maçã" },
  { name: "beef", icon: Beef, label: "Carne" },
  { name: "cake", icon: Cake, label: "Bolo" },
  { name: "coffee", icon: Coffee, label: "Café" },
  { name: "cookie", icon: Cookie, label: "Cookie" },
  { name: "cup-soda", icon: CupSoda, label: "Bebida" },
  { name: "egg", icon: Egg, label: "Ovo" },
  { name: "fish", icon: Fish, label: "Peixe" },
  { name: "flame", icon: Flame, label: "Quente" },
  { name: "grape", icon: Grape, label: "Uva" },
  { name: "ice-cream", icon: IceCream, label: "Sorvete" },
  { name: "leaf", icon: Leaf, label: "Vegetal" },
  { name: "milk", icon: Milk, label: "Leite" },
  { name: "pizza", icon: Pizza, label: "Pizza" },
  { name: "salad", icon: Salad, label: "Salada" },
  { name: "sandwich", icon: Sandwich, label: "Sanduíche" },
  { name: "soup", icon: Soup, label: "Sopa" },
  { name: "utensils-crossed", icon: UtensilsCrossed, label: "Refeição" },
  { name: "wheat", icon: Wheat, label: "Grão" },
  { name: "cherry", icon: Cherry, label: "Cereja" },
];

export function getIconComponent(iconName: string | null | undefined) {
  if (!iconName) return null;
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found?.icon || null;
}

interface RecipeIconPickerProps {
  selectedIcon: string | null;
  onSelectIcon: (icon: string | null) => void;
  bannerFile: File | null;
  onBannerFileChange: (file: File | null) => void;
  bannerPreview: string | null;
}

const RecipeIconPicker = ({ selectedIcon, onSelectIcon, bannerFile, onBannerFileChange, bannerPreview }: RecipeIconPickerProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onBannerFileChange(file);
    if (file) onSelectIcon(null); // clear icon when image is selected
  };

  const clearBanner = () => {
    onBannerFileChange(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-foreground">Ícone da receita</p>
      <div className="flex flex-wrap gap-2">
        {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
          <button
            key={name}
            type="button"
            onClick={() => { onSelectIcon(name); clearBanner(); }}
            title={label}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all border ${
              selectedIcon === name && !bannerFile
                ? "border-primary bg-primary/20 text-primary scale-110"
                : "border-border bg-muted/50 text-muted-foreground hover:border-primary/50 hover:text-primary"
            }`}
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </div>

      <div className="pt-1">
        <p className="text-xs font-semibold text-foreground mb-2">ou envie uma imagem de banner</p>
        <ImageDropzone
          onFileSelect={(file) => {
            onBannerFileChange(file);
            if (file) onSelectIcon(null);
            if (!file && fileInputRef.current) fileInputRef.current.value = "";
          }}
          preview={bannerPreview}
          label="Arraste ou clique para enviar"
          hint="PNG, JPG ou WEBP"
          aspectRatio="aspect-[16/5]"
          compact
        />
      </div>
    </div>
  );
};

export default RecipeIconPicker;
